import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronDown, ChevronRight, ArrowRight } from "lucide-react";
import { PortalStatusSelect } from "@/components/portal/PortalStatusSelect";

const STYLE = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200", in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  review: "bg-sky-50 text-[#006090] border-sky-200", attention: "bg-rose-50 text-rose-700 border-rose-200",
  upcoming: "bg-slate-50 text-slate-400 border-slate-200", not_started: "bg-white text-slate-600 border-slate-300",
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const Pill = ({ r }) => <span className={`inline-block whitespace-nowrap text-[11px] font-semibold border rounded-full px-2 py-0.5 ${STYLE[r.status_key]}`} data-testid={`checklist-status-${r.id}`}>{r.status_label}</span>;

export function MyChecklist({ goTo }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [sel, setSel] = useState(null);
  const load = useCallback(() => { api.get("/portal/checklist").then((r) => setData(r.data)).catch(() => setData({ phases: [] })); }, []);
  useEffect(() => { load(); }, [load]);
  if (data === null) return <p className="text-sm text-slate-400">Loading your checklist…</p>;
  if (!data.phases.length) return <p className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500 text-center" data-testid="checklist-empty">Your checklist will appear here once your program is set up.</p>;
  const all = data.phases.flatMap((p) => p.rows);
  const done = all.filter((r) => r.status_key === "completed").length;
  return (
    <div className="space-y-4" data-testid="my-checklist">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div><h3 className="font-display text-lg font-semibold text-slate-900">{data.program_label ? `${data.program_label} Checklist` : "My Checklist"}</h3>
          <p className="text-xs text-slate-500">Click any active item to update it. Items marked for district review are verified by your district team.</p></div>
        <p className="text-sm text-slate-600" data-testid="checklist-progress"><b>{done}</b> of {all.length} completed</p>
      </div>
      {data.phases.map((p) => {
        const isOpen = open[p.name] ?? (p.is_current || (!data.phases.some((x) => x.is_current) && p === data.phases[0]));
        return (
          <div key={p.name} className={`rounded-xl border bg-white overflow-hidden ${p.is_current ? "border-[#006090]" : "border-slate-200"}`} data-testid={`checklist-phase-${p.name}`}>
            <button className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50" onClick={() => setOpen((o) => ({ ...o, [p.name]: !isOpen }))} data-testid={`checklist-phase-toggle-${p.name}`}>
              <span className="flex items-center gap-2 font-semibold text-sm text-slate-900">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}{p.name}
                {p.is_current && <span className="text-[10px] font-bold uppercase text-[#006090] bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5">Current</span>}</span>
              <span className="text-xs text-slate-500">{p.done}/{p.total}</span>
            </button>
            {isOpen && <Rows rows={p.rows} onSelect={setSel} />}
          </div>
        );
      })}
      {sel && <RowDialog row={sel} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); }} goTo={goTo} />}
    </div>
  );
}

function Rows({ rows, onSelect }) {
  return (
    <div className="border-t border-slate-100">
      <div className="hidden sm:grid grid-cols-[1fr_190px_120px] px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 bg-slate-50">
        <span>Checklist Item</span><span>Status</span><span>Completion Date</span>
      </div>
      {rows.map((r) => {
        const active = r.status_key !== "upcoming";
        return (
          <button key={r.id} disabled={!active} onClick={() => onSelect(r)} data-testid={`checklist-row-${r.id}`}
            className={`w-full text-left grid grid-cols-1 sm:grid-cols-[1fr_190px_120px] gap-1 sm:gap-0 px-4 py-2.5 border-t border-slate-100 text-sm items-center ${active ? "hover:bg-slate-50" : "cursor-default opacity-70"}`}>
            <span className={`font-medium ${r.status_key === "completed" ? "text-slate-500" : "text-slate-800"}`}>{r.title}</span>
            <span><Pill r={r} /></span>
            <span className={`text-xs text-slate-500 ${r.completed_at ? "" : "hidden sm:block"}`} data-testid={`checklist-date-${r.id}`}><span className="sm:hidden">Completed: </span>{fmt(r.completed_at)}</span>
          </button>
        );
      })}
    </div>
  );
}

function RowDialog({ row, onClose, onDone, goTo }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="checklist-dialog">
        <DialogHeader><DialogTitle>{row.title}</DialogTitle><DialogDescription>{row.summary || "Complete this step on your journey."}</DialogDescription></DialogHeader>
        <div className="flex items-center gap-2 text-sm">Status: <Pill r={row} /></div>
        {row.official && <p className="text-xs text-slate-500">Your district team verifies this requirement after you complete your part.</p>}
        {row.can_update ? (
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <PortalStatusSelect taskId={row.id} status={row.status_key} needsUpload={row.needs_submission} onChanged={onDone} />
            {row.needs_submission && <Button className="bg-[#006090] hover:bg-[#004E75]" onClick={() => goTo("tasks", null, { taskId: row.id })} data-testid="checklist-open-task">Upload document <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          </div>
        ) : <p className="text-xs text-slate-500 pt-2">{row.status_key === "completed" ? `Completed ${fmt(row.completed_at)}.` : "No action is needed from you right now."}</p>}
        <button className="text-xs text-[#006090] hover:underline text-left" onClick={() => goTo("tasks", null, { taskId: row.id })} data-testid="checklist-task-details">Open full task details</button>
      </DialogContent>
    </Dialog>
  );
}
